import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import type { DealerLocatorClaimStore } from './dealerLocatorApi';

export type DirectoryStatus = 'authorized' | 'locator' | 'distributor' | 'none' | 'api_pending';

export interface DirectorySource {
  brandId: string;
  brandName: string;
  status: DirectoryStatus;
  url?: string;
  listUrl?: string;
  parser?: string;
  coversBrandIds?: string[];
  /** Other ids the same brand goes by (omsystem, olympus). */
  aliases?: string[];
  ingested?: boolean;
  notes?: string;
  logoUrl?: string;
  /** Codes on this brand's list or locator that Motzu reads into designations (`BRAND_TIER_CODE_RULES`) */
  tierCodes?: DirectoryTierCode[];
  claims: { unverified: number; pending: number; verified: number };
  stores: DealerLocatorClaimStore[];
}

export interface DirectoryTierCode {
  code: string;
  label: string;
  /** Empty: the code only restates the base authorization */
  tiers: string[];
  categoryScope?: string[];
}

/**
 * A catalog brand with no row in `sources` and no row in the dealer-locator
 * registry — letter-only purely by fallback, so a store's only verification
 * path for it is otherwise invisible on this page.
 */
export interface LetterOnlyBrand {
  brandId: string;
  brandName: string;
  claims: { unverified: number; pending: number; verified: number };
  stores: DealerLocatorClaimStore[];
}

export interface DirectoryImport {
  brandId: string;
  count: number;
  listDate: string | null;
  sourceUrl: string | null;
  method: 'fetch' | 'upload';
  importedAt: string | null;
  warnings?: string[];
}

async function readError(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => ({}));
  return body?.error?.message || body?.message || fallback;
}

export const brandDealerDirectoryApi = {
  list: async () => {
    const response = await makeApiCall('/admin/brand-dealer-directory', { headers: await getAuthHeaders() });
    if (!response.ok) throw new Error(await readError(response, 'Failed to load dealer lists'));
    const body = await response.json();
    return body.data as {
      sources: DirectorySource[];
      letterOnlyBrands: LetterOnlyBrand[];
      imports: Record<string, DirectoryImport>;
      claims?: Record<string, DirectorySource['claims']>;
      stores?: Record<string, DealerLocatorClaimStore[]>;
    };
  },
  fetchList: async (brandId: string) => {
    const response = await makeApiCall(`/admin/brand-dealer-directory/${encodeURIComponent(brandId)}/fetch`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error(await readError(response, 'Fetch failed'));
    return response.json();
  },
  upload: async (brandId: string, file: File, opts: { listDate?: string; sourceUrl?: string }) => {
    const form = new FormData();
    form.append('file', file);
    if (opts.listDate) form.append('listDate', opts.listDate);
    if (opts.sourceUrl) form.append('sourceUrl', opts.sourceUrl);
    const response = await makeApiCall(`/admin/brand-dealer-directory/${encodeURIComponent(brandId)}/upload`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: form,
    });
    if (!response.ok) throw new Error(await readError(response, 'Upload failed'));
    return response.json();
  },
  sweep: async () => {
    const response = await makeApiCall('/admin/brand-dealer-directory/sweep', {
      method: 'POST',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error(await readError(response, 'Check failed'));
    return response.json();
  },
};
